import { cn } from '@atlas/ui-primitives'
import { UploadCloud } from 'lucide-react'
import { type DragEvent, type ReactNode, useId, useState } from 'react'

export interface UploadDropzoneProps {
	/** Called with the picked or dropped files. Never called while disabled. */
	onFiles: (files: File[]) => void
	/**
	 * Accessible name for the file input — what the surface is FOR ("Add files to
	 * classify"), which is what a screen-reader user needs and is often more
	 * specific than the visible line.
	 */
	label: string
	/** Visible prompt. Defaults to `label` when the two would say the same thing. */
	prompt?: ReactNode
	/** Secondary line under the prompt — e.g. what happens after dropping. */
	hint?: ReactNode
	/**
	 * `panel` is the full-width dashed area a page or dialog gives to uploading;
	 * `compact` is the slimmer bar that sits above an existing list; `tile` is the
	 * square cell that sits IN a grid of assets, sized like the tiles beside it.
	 */
	variant?: 'panel' | 'compact' | 'tile'
	/** Single-file surfaces (the campaign dialog) pass false. */
	multiple?: boolean
	accept?: string
	isDisabled?: boolean
	/** Marks the control invalid, for a surface that validates the selection. */
	isInvalid?: boolean
	/**
	 * Ids of the guidance text and any rejection alert, so the error is announced
	 * as part of the input's description rather than living somewhere a screen
	 * reader never connects to the control.
	 */
	describedBy?: string
	/** Test hook for surfaces whose visible copy is not a stable selector. */
	inputTestId?: string
	/** Replaces the default prompt entirely, for a surface with its own copy. */
	children?: ReactNode
}

/**
 * A drop surface that is also a file picker.
 *
 * Both interactions on one element, because they are one affordance. Every
 * upload surface had implemented one half or duplicated both — four versions
 * with four sets of keyboard handling, drag state, and input-reset logic to keep
 * in sync.
 *
 * **Activation is the browser's own `<label>` behaviour**, not a synthetic click
 * on a hidden input. The input stays visible inside the surface and is the only
 * focusable thing here: no `role`, no `tabIndex`, no second tab stop, and a
 * keyboard user operates a real file input rather than a button that opens one
 * by script. That also makes `aria-invalid` and `aria-describedby` meaningful —
 * they attach to the control the user actually focuses, which is what lets a
 * validating surface announce *why* a selection was rejected. This pattern came
 * from the campaign dialog's own drop zone, which had it right; adopting it here
 * is what made the two interchangeable.
 *
 * Deliberately knows nothing about uploading. It hands over `File[]` and stops
 * there, which is what lets the same component serve a surface that uploads
 * immediately and one that stages files for classification first.
 */
export function UploadDropzone({
	onFiles,
	label,
	prompt,
	hint,
	variant = 'panel',
	multiple = true,
	accept,
	isDisabled = false,
	isInvalid = false,
	describedBy,
	inputTestId,
	children,
}: UploadDropzoneProps) {
	const [isDragOver, setIsDragOver] = useState(false)
	const inputId = useId()

	const take = (list: FileList | null) => {
		if (!list?.length || isDisabled) return
		onFiles(Array.from(list))
	}

	// preventDefault on both dragover and drop, so a dropped file never navigates
	// the browser away from the application.
	const onDragOver = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault()
		if (!isDisabled) setIsDragOver(true)
	}

	return (
		<label
			htmlFor={inputId}
			onDragEnter={onDragOver}
			onDragOver={onDragOver}
			onDragLeave={() => setIsDragOver(false)}
			onDrop={(event) => {
				event.preventDefault()
				setIsDragOver(false)
				take(event.dataTransfer.files)
			}}
			className={cn(
				'flex w-full flex-col items-center gap-ds-2 rounded-ds-control border border-dashed text-center transition-colors',
				variant === 'panel' && 'p-ds-6',
				variant === 'compact' && 'p-ds-4',
				// A grid cell, so it must square up with the tiles it sits beside - same radius,
				// same lift on hover - and carry its own focus ring: the input it activates is
				// not visible in this variant.
				variant === 'tile' &&
					'aspect-square justify-center rounded-ds-base p-ds-3 transition-[transform,translate,border-color,background-color] duration-(--ds-motion-duration-fast) hover:-translate-y-0.5 focus-within:ring-2 focus-within:ring-ds-ring',
				isDragOver ? 'border-ds-primary bg-ds-primary/5' : 'border-ds-border bg-ds-surface',
				!isDisabled && !isDragOver && 'cursor-pointer hover:border-ds-primary hover:bg-ds-primary/5',
				isDisabled && 'opacity-60',
			)}
		>
			{children ?? (
				<>
					{variant === 'tile' && <UploadCloud aria-hidden className="size-6 text-ds-text-muted" strokeWidth={1.5} />}
					<p className={cn('text-ds-text', variant === 'tile' ? 'text-ds-xs leading-snug' : 'text-ds-sm')}>
						{prompt ?? label}
					</p>
					{hint && <p className="text-ds-xs text-ds-text-muted">{hint}</p>}
				</>
			)}
			<input
				id={inputId}
				type="file"
				multiple={multiple}
				accept={accept}
				disabled={isDisabled}
				aria-invalid={isInvalid || undefined}
				aria-describedby={describedBy}
				data-testid={inputTestId}
				aria-label={label}
				onChange={(event) => {
					take(event.target.files)
					// Reset so re-picking the same file still fires a change event.
					event.target.value = ''
				}}
				// In a tile the native input has no room to be legible; the whole square is the
				// affordance, and the input stays a real focusable control behind it.
				className={variant === 'tile' ? 'sr-only' : 'max-w-full text-ds-xs text-ds-text-muted'}
			/>
			{/* Pointer-only affordance: a visible text cue so drag-active is never
			    signalled by colour alone, hidden from assistive technology because it
			    has no keyboard equivalent and must not alter the input's name. */}
			{isDragOver && (
				<span aria-hidden="true" className="text-ds-xs text-ds-text-muted">
					Release to drop
				</span>
			)}
		</label>
	)
}
